/**
 * RTReadinessPanel — Verificação de licença Microsoft/Teams + activação
 * do cronjob de polling automático das gravações Teams.
 *
 * Aparece na página `/settings/office365` (EmailSettings). Permite ao
 * admin:
 *   1. Verificar se o tenant Microsoft tem SKUs Teams-enabled
 *   2. Validar as permissões da App Registration (Organization.Read.All,
 *      OnlineMeetings.Read.All)
 *   3. Activar/desactivar per-user o polling automático + configurar o
 *      UPN Microsoft de cada gestor
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Mic,
  Radio,
  Shield,
  XCircle,
} from "lucide-react";


export default function RTReadinessPanel() {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [testUpn, setTestUpn] = useState("");

  const [users, setUsers] = useState([]);
  const [pollingUsers, setPollingUsers] = useState([]);
  const [toggling, setToggling] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const [allR, pollR] = await Promise.all([
        api.get("/users"),
        api.get("/office365/rt-polling-users"),
      ]);
      // Filter to roles that make sense for polling (gestor + direcao + admin)
      const eligible = (allR.data || []).filter(
        (u) => ["admin", "gestor", "direcao"].includes(u.role),
      );
      setUsers(eligible);
      setPollingUsers(pollR.data.users || []);
    } catch (e) {
      console.error("Failed to load users", e);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const runCheck = async () => {
    setChecking(true);
    setResult(null);
    try {
      const { data } = await api.post(
        "/office365/rt-readiness-check",
        testUpn ? { user_upn: testUpn.trim() } : {},
      );
      setResult(data);
      if (data.ready) {
        toast.success("Configuração pronta para RT polling.");
      } else {
        toast.warning(data.verdict);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha na verificação.");
    } finally {
      setChecking(false);
    }
  };

  const togglePolling = async (user, enabled) => {
    const currentUpn = user.office365?.user_upn || user.email;
    if (enabled && !currentUpn) {
      toast.error(`Configure primeiro o UPN Microsoft de ${user.name}.`);
      return;
    }
    setToggling(user.id);
    try {
      await api.post("/office365/rt-polling-toggle", {
        user_id: user.id,
        enabled,
        user_upn: currentUpn,
      });
      toast.success(
        `Polling RT ${enabled ? "activado" : "desactivado"} para ${user.name}.`,
      );
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao actualizar.");
    } finally {
      setToggling(null);
    }
  };

  const setUserUpn = async (user, upn) => {
    setToggling(user.id);
    try {
      const enabled = pollingUsers.some((u) => u.id === user.id);
      await api.post("/office365/rt-polling-toggle", {
        user_id: user.id,
        enabled,
        user_upn: upn,
      });
      toast.success(`UPN actualizado para ${user.name}.`);
      await loadUsers();
    } catch (err) {
      toast.error("Falha a guardar UPN.");
    } finally {
      setToggling(null);
    }
  };

  return (
    <Card className="p-6 space-y-5 border-border" data-testid="rt-readiness-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Mic className="w-5 h-5 text-primary" />
            <h2 className="font-display text-xl text-foreground">
              Agente IA · Análise da Reunião Técnica
            </h2>
          </div>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Verifique se a sua licença Microsoft e configuração Azure AD estão
            prontas para o cronjob automático de RT (baixa gravações Teams
            15/15min + processa via Whisper + Claude).
          </p>
        </div>
      </div>

      {/* Readiness check */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
          <label className="flex-1 block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">
              UPN a testar (opcional)
            </div>
            <input
              type="email"
              placeholder="gestor@caring.pt"
              value={testUpn}
              onChange={(e) => setTestUpn(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-card border border-border focus:border-primary focus:outline-none text-sm"
              data-testid="rt-readiness-upn-input"
            />
            <div className="text-xs text-muted-foreground mt-1">
              Se preencher, o check inclui o teste de OnlineMeetings.Read.All
              + Application Access Policy para este utilizador.
            </div>
          </label>
          <Button
            onClick={runCheck}
            disabled={checking}
            className="bg-primary hover:bg-[#082848] text-primary-foreground"
            data-testid="rt-readiness-check-btn"
          >
            {checking ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A verificar…</>
            ) : (
              <><Shield className="w-4 h-4 mr-2" />Verificar preparação</>
            )}
          </Button>
        </div>

        {result && (
          <div
            className={`p-4 rounded-lg border ${result.ready ? "border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))]" : "border-[hsl(var(--warning))]/25 bg-[hsl(var(--warning-bg))]"}`}
            data-testid="rt-readiness-result"
          >
            <div className={`font-semibold text-sm mb-2 flex items-center gap-2 ${result.ready ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>
              {result.ready ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              {result.verdict}
            </div>

            {result.skus?.length > 0 && (
              <div className="mb-3 text-xs">
                <div className="font-semibold text-foreground mb-1">Licenças detectadas:</div>
                <ul className="space-y-1">
                  {result.skus.map((s) => (
                    <li key={s.sku} className="flex items-center gap-2">
                      <Badge className="bg-[hsl(var(--info-bg))] text-[hsl(var(--info))] border-0 text-[10px]">
                        {s.consumed_units}/{s.prepaid_units}
                      </Badge>
                      <span className="text-foreground">{s.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-1.5">
              {result.checks.map((c, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  {c.ok
                    ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                    : <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />}
                  <div className="flex-1">
                    <div className="font-medium text-foreground">{c.name}</div>
                    <div className="text-muted-foreground">{c.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Per-user toggle */}
      <div className="pt-4 border-t border-border">
        <div className="flex items-center gap-2 mb-3">
          <Radio className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm text-foreground">
            Polling automático por utilizador
          </h3>
          <Badge className="bg-muted text-foreground border-0 text-[10px]">
            {pollingUsers.length} activos
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground mb-3">
          Utilizadores com <code className="bg-muted px-1 rounded">rt_polling_enabled=true</code> terão as suas
          reuniões Teams verificadas de 15 em 15 minutos. Certifique-se que
          a "Application Access Policy" Azure inclui cada UPN listado abaixo.
        </div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {users.length === 0 && (
            <div className="text-xs text-muted-foreground italic">Sem utilizadores elegíveis.</div>
          )}
          {users.map((u) => {
            const isEnabled = pollingUsers.some((p) => p.id === u.id);
            const upn = u.office365?.user_upn || "";
            const isThisToggling = toggling === u.id;
            return (
              <div
                key={u.id}
                className={`flex flex-wrap items-center gap-3 p-3 rounded-lg border ${isEnabled ? "border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))]/40" : "border-border bg-card"}`}
                data-testid={`rt-polling-user-${u.email}`}
              >
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium text-sm text-foreground">{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.email} · {u.role}</div>
                </div>
                <input
                  type="email"
                  placeholder="UPN Microsoft"
                  defaultValue={upn}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== upn) setUserUpn(u, v);
                  }}
                  className="px-3 py-1.5 rounded border border-border text-xs w-56"
                  data-testid={`rt-polling-upn-${u.email}`}
                />
                <Button
                  size="sm"
                  variant={isEnabled ? "default" : "outline"}
                  onClick={() => togglePolling(u, !isEnabled)}
                  disabled={isThisToggling}
                  className={isEnabled ? "bg-emerald-600 hover:bg-emerald-700 text-primary-foreground" : ""}
                  data-testid={`rt-polling-toggle-${u.email}`}
                >
                  {isThisToggling
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : isEnabled ? "Activo" : "Activar"}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Help */}
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer hover:text-foreground font-medium">
          Como configurar a App Registration Azure AD?
        </summary>
        <ol className="mt-2 space-y-1 pl-4 list-decimal">
          <li>Vá a <a href="https://portal.azure.com" target="_blank" rel="noreferrer" className="underline">portal.azure.com</a> → Azure Active Directory → App registrations → New registration</li>
          <li>Nome: <code>Caring RT Notes</code>. Supported account types: <em>Single tenant</em>. Redirect URI: deixe em branco.</li>
          <li>Após criar, vá a "Certificates &amp; secrets" → New client secret → guarde o valor no campo Client Secret acima.</li>
          <li>Vá a "API permissions" → Add permission → Microsoft Graph → Application permissions → adicione:
            <ul className="list-disc pl-4 mt-1 space-y-0.5">
              <li><code>Organization.Read.All</code></li>
              <li><code>OnlineMeetings.Read.All</code></li>
              <li><code>OnlineMeetingRecording.Read.All</code></li>
            </ul>
          </li>
          <li>Clique em <strong>Grant admin consent for &lt;seu tenant&gt;</strong>.</li>
          <li>Para cada UPN gestor listado acima, aplique a <em>Application Access Policy</em> via PowerShell (ver <a href="https://learn.microsoft.com/en-us/graph/cloud-communication-online-meeting-application-access-policy" target="_blank" rel="noreferrer" className="underline">docs</a>).</li>
          <li>Volte aqui e clique em "Verificar preparação".</li>
        </ol>
      </details>
    </Card>
  );
}
